import { useEffect } from 'react';
import type { ChatAnchor } from '@wundo/shared/chat.ts';

/*
 * The page the chat is currently sitting beside. One drawer, one page behind
 * it, so one answer — held in the module rather than in a context, for the same
 * reason the drawer's own open state is: whoever sends a message has to be able
 * to ask, and most of them do not render anything.
 */
let current: ChatAnchor | null = null;

/** What the user has open, or null when it is nothing the chat knows about. */
export function currentChatAnchor(): ChatAnchor | null {
  return current;
}

/** Say what is on screen. Pages do this through `usePublishChatAnchor`. */
export function setChatAnchor(anchor: ChatAnchor | null): void {
  current = anchor;
}

/**
 * Publish this page's anchor for as long as the page is on screen.
 *
 * Cleared on the way out, because leaving the page has to be said as clearly as
 * arriving on it: the next message the user sends from anywhere else must carry
 * nothing. Only cleared when the anchor still is this page's own — a page that
 * has already handed over to another must not wipe the newer one.
 */
export function usePublishChatAnchor(anchor: ChatAnchor | null): void {
  const type = anchor?.type ?? null;
  const id = anchorId(anchor);

  useEffect(() => {
    const published = type === null ? null : rebuild(type, id);
    if (!published) return;

    setChatAnchor(published);

    return () => {
      if (current?.type === type && anchorId(current) === id) {
        setChatAnchor(null);
      }
    };
  }, [type, id]);
}

/** What a page names, for the anchors that name something. */
function anchorId(anchor: ChatAnchor | null): string | null {
  if (!anchor) return null;
  return 'id' in anchor ? anchor.id : null;
}

/**
 * The anchor again, out of the two plain values the effect watches. Kept out of
 * the dependencies as an object, because a page that rebuilds its anchor every
 * render would otherwise republish it every render.
 */
function rebuild(type: ChatAnchor['type'], id: string | null): ChatAnchor | null {
  switch (type) {
    case 'meeting':
    case 'journey':
    case 'person':
      return id ? { type, id } : null;
    case 'practice':
      return { type };
  }
}
